package com.elan.edubridge.ui.platform

import com.elan.edubridge.shared.di.SharedGraph
import com.elan.edubridge.shared.domain.model.AcademyApplication
import com.elan.edubridge.shared.domain.model.CommandFailure
import com.elan.edubridge.ui.shell.escapeHtml
import com.elan.edubridge.ui.shell.icon
import com.elan.edubridge.ui.shell.readErrorHtml
import com.elan.edubridge.ui.shell.wireRetry
import com.elan.edubridge.ui.staff.Toast
import kotlinx.browser.document
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import org.w3c.dom.HTMLButtonElement
import org.w3c.dom.HTMLElement

/**
 * `/app/admin/applications`의 학원 개설 승인 큐다.
 *
 * [AcademyApplication] 계약이 노출하는 학원명·신청자 UID·연락처·신청 일시·ID만 표시한다.
 * 신청 당시의 대표자명·주소·소개는 읽기 모델에 없으므로 추정해 보여주지 않는다.
 */
object PlatformApplicationsPage {
    private val scope = MainScope()
    private val repository = SharedGraph.createRepository()
    private val commandRepository = SharedGraph.createAcademyCommandRepository()

    private var applications: List<AcademyApplication> = emptyList()
    private var selectedApplicationId: String? = null

    /** 마지막 조회의 실패. 승인 대기 큐가 빈 것과 못 읽은 것을 가른다(#30). */
    private var readFailure: CommandFailure? = null

    /** 조회 실패의 "다시 시도" 버튼 id. */
    private const val RETRY_ID = "platform-applications-retry"

    fun open(session: com.elan.edubridge.shared.domain.model.SessionState) {
        PlatformDashboardShell.render(session, PlatformDashboardShell.Page.ACADEMY_APPLICATIONS, loadingHtml())
        scope.launch {
            val result = repository.pendingAcademyApplicationsResult()
            readFailure = result.failure
            applications = result.items
                .sortedWith(compareBy<AcademyApplication> { it.createdAt.ifBlank { "\uFFFF" } }.thenBy { it.id })
            selectedApplicationId = selectedApplicationId?.takeIf { selectedId ->
                applications.any { it.id == selectedId }
            }
            renderQueue(session)
        }
    }

    private fun renderQueue(session: com.elan.edubridge.shared.domain.model.SessionState) {
        PlatformDashboardShell.render(
            session,
            PlatformDashboardShell.Page.ACADEMY_APPLICATIONS,
            queueHtml(applications, selectedApplicationId),
        )
        wireRetry(RETRY_ID) { open(session) }
        wireQueue(session)
    }

    private fun wireQueue(session: com.elan.edubridge.shared.domain.model.SessionState) {
        (document.getElementById("refresh-applications") as? HTMLButtonElement)?.onclick = {
            open(session)
            null
        }

        val selectionButtons = document.querySelectorAll("button[data-application-id]")
        for (index in 0 until selectionButtons.length) {
            val button = selectionButtons.item(index) as? HTMLButtonElement ?: continue
            button.onclick = {
                selectedApplicationId = button.getAttribute("data-application-id")
                renderQueue(session)
                null
            }
        }

        (document.getElementById("approve-selected-application") as? HTMLButtonElement)?.onclick = {
            selectedApplicationId?.let { approveSelected(session, it) }
            null
        }
    }

    private fun approveSelected(session: com.elan.edubridge.shared.domain.model.SessionState, applicationId: String) {
        val button = document.getElementById("approve-selected-application") as? HTMLButtonElement
        val error = document.getElementById("approve-error") as? HTMLElement
        button?.disabled = true
        error?.setAttribute("hidden", "")

        scope.launch {
            val result = commandRepository.approveAcademyApplication(applicationId)
            if (result.isSuccess) {
                Toast.success("학원 신청을 승인했어요.")
                selectedApplicationId = null
                open(session)
            } else {
                button?.disabled = false
                error?.textContent = result.errorMessage ?: "승인에 실패했어요. 다시 시도해 주세요."
                error?.removeAttribute("hidden")
            }
        }
    }

    private fun loadingHtml(): String = """
        <div class="eb-page-header"><div><h1 class="eb-page-title">학원 승인</h1><p class="eb-meta">개설 신청 목록을 불러오는 중이에요.</p></div></div>
        <section class="eb-card eb-platform-loading" aria-live="polite">${icon("icon-refresh", "md", "eb-spin")}<span>대기 중인 신청을 불러오는 중이에요.</span></section>
    """.trimIndent()

    private fun queueHtml(items: List<AcademyApplication>, selectedId: String?): String {
        val selected = items.firstOrNull { it.id == selectedId }
        // 승인 대기 큐를 못 읽는 것은 "대기 중인 신청이 없다"와 정반대의 운영 정보다(#30) —
        // 학원장들이 승인을 기다리는 동안 관리자는 큐가 비었다고 믿게 된다.
        val list = if (items.isEmpty()) {
            readFailure?.let { readErrorHtml(it, RETRY_ID) } ?: emptyHtml()
        } else items.joinToString("") { application ->
            val selectedClass = if (application.id == selectedId) " eb-platform-request--selected" else ""
            val pressed = application.id == selectedId
            """
                <li>
                    <button class="eb-platform-request$selectedClass" data-application-id="${application.id.escapeHtml()}" aria-pressed="$pressed">
                        <strong>${application.academyName.displayOrUnknown()}</strong>
                        <span>${application.createdAt.displayOrUnknown()}</span>
                    </button>
                </li>
            """.trimIndent()
        }
        return """
            <div class="eb-page-header">
                <div><h1 class="eb-page-title">학원 승인</h1><p class="eb-meta">대기 중인 신청을 선택해 세부 내용을 확인하고 승인하세요.</p></div>
                <button class="eb-button eb-button--ghost" id="refresh-applications">${icon("icon-refresh", "sm")}새로고침</button>
            </div>
            <section class="eb-platform-approval" aria-label="학원 개설 승인 요청">
                <div class="eb-card eb-platform-approval__list">
                    <div class="eb-platform-approval__heading"><h2>승인 요청</h2><span class="eb-badge-count">${items.size}</span></div>
                    <ul class="eb-platform-request-list">$list</ul>
                </div>
                <div class="eb-card eb-platform-approval__detail">${detailHtml(selected)}</div>
            </section>
        """.trimIndent()
    }

    private fun emptyHtml(): String = """
        <li class="eb-empty-state">${icon("icon-academy", "xl")}<h2>대기 중인 승인 요청이 없어요</h2><p>새로운 신청이 접수되면 이 목록에 표시돼요.</p></li>
    """.trimIndent()

    private fun detailHtml(application: AcademyApplication?): String {
        if (application == null) {
            return """
                <div class="eb-empty-state">${icon("icon-academy", "xl")}<h2>승인 요청을 선택해 주세요</h2><p>왼쪽 목록에서 요청을 선택하면 신청 정보를 확인할 수 있어요.</p></div>
            """.trimIndent()
        }
        return """
            <div class="eb-platform-approval__heading"><h2>신청 상세</h2><span class="eb-chip eb-chip--warning">승인 대기</span></div>
            <dl class="eb-platform-detail-list">
                <div><dt>학원명</dt><dd>${application.academyName.displayOrUnknown()}</dd></div>
                <div><dt>신청자 UID</dt><dd>${application.applicantUid.displayOrUnknown()}</dd></div>
                <div><dt>연락처</dt><dd>${application.contactPhone.displayOrUnknown()}</dd></div>
                <div><dt>신청 일시</dt><dd>${application.createdAt.displayOrUnknown()}</dd></div>
                <div><dt>신청 ID</dt><dd>${application.id.displayOrUnknown()}</dd></div>
            </dl>
            <p class="eb-form-error" role="alert" id="approve-error" hidden></p>
            <button class="eb-button eb-button--primary" id="approve-selected-application">${icon("icon-check", "sm")}선택한 신청 승인</button>
        """.trimIndent()
    }

    private fun String?.displayOrUnknown(): String = this?.takeIf { it.isNotBlank() }?.escapeHtml() ?: "정보 없음"
}
